// ─── Canonical catalog text-match core ──────────────────────────────────────
//
// ONE implementation of query normalization, tokenization, and field-weighted
// scoring for every catalog search surface. Consumers:
//
//   Worker  — src/lib/catalog-tools.js (MCP search_catalog + AI assistant)
//             imports this file directly (wrangler bundles it).
//   Web     — web/src/components/SearchModal.tsx (⌘K), routes/MapMode.tsx
//             (map search overlay), routes/Browse.tsx (?q= filter),
//             components/DestinationPicker.tsx, components/BookingEditor.tsx
//             import via the `@core` alias (web/vite.config.ts + tsconfig).
//   Mobile  — mobile/lib/search-core.js is a BYTE-IDENTICAL COPY of this file
//             (Metro can't import outside mobile/ without config that risks
//             the OTA fingerprint). After editing this file, run:
//                 scripts/sync_search_core.sh
//             which re-copies it + the .d.ts into mobile/lib/ and fails if
//             the copies would drift. NEVER hand-edit mobile/lib/search-core.js.
//
// History: this logic previously existed as 7 hand-rolled forks (mobile
// search.ts, web SearchModal, web MapMode overlay, web Browse, web
// DestinationPicker, web BookingEditor, worker catalog-tools), each missing
// pieces the others had — diacritic folding, apostrophe stripping, CJK
// tokenization, stopword handling, fuzzy fallback. Unified 2026-07-12.
//
// Pure module: no I/O, no platform APIs, no imports. Safe for Workers,
// Vite, Metro, and plain `node --check`.

// Fold diacritics + width so an ASCII query matches macron-spelled names:
// "gora" ⇢ "Gōra", "miyamaso" ⇢ "Miyamasō", full-width "ＧＩＮＺＡ" ⇢ "GINZA".
// NFKD decomposes base + combining mark (and folds width/compat forms), the
// strip removes only the Latin combining range (U+0300–U+036F), NFC
// re-composes kana dakuten (が = か+゙ decomposes OUTSIDE that range, so
// Japanese text round-trips untouched; half-width ｶﾞ recomposes to ガ).
export function foldDiacritics(s) {
  return String(s ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFC');
}

// Normalize a string for index/query comparison: fold, lowercase, strip
// apostrophes (straight + curly + tick + Hawaiian okina) so "quinns" /
// "quinn's" / "Quinn's" all land on the same bytes. Display strings keep
// their original casing/punctuation — this is comparison-side only.
export function normalizeIndexText(s) {
  return foldDiacritics(s)
    .toLowerCase()
    .replace(/['’`\u02bb\u02bc]/g, '');
}

// Split a query into match tokens. Latin/digit runs plus CJK (hiragana,
// katakana + chōon, kanji) and Hangul runs survive; everything else is a
// separator. Because the input goes through normalizeIndexText first, the
// tokens land in the same space as indexed haystacks.
export function tokenize(query) {
  return normalizeIndexText(query)
    .split(/[^a-z0-9ぁ-んァ-ヶー一-龯가-힣ㄱ-ㅎㅏ-ㅣ]+/)
    .filter((t) => t.length > 0);
}

// Stopwords stripped from multi-word queries so "Asaba ryokan" matches
// Asaba even though "ryokan" isn't in its haystack (the strict-AND
// false-miss). Includes generic query filler AND venue-category nouns.
export const SEARCH_STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'for',
  'with', 'near', 'around', 'by', 'from', 'is', 'are', 'be', 'some',
  'any', 'good', 'best', 'top', 'great', 'nice',
  'restaurant', 'restaurants', 'place', 'places', 'food', 'spot', 'spots',
  'ryokan', 'ryokans', 'hotel', 'hotels', 'inn', 'inns',
]);

// Strip stopwords with the all-stopwords fallback: when EVERY token is a
// stopword ("ryokan" alone), keep the originals so category-word browsing
// still works (the kind/tag scoring rungs pick them up).
export function applyStopwords(tokens) {
  const kept = tokens.filter((t) => !SEARCH_STOPWORDS.has(t));
  return kept.length > 0 ? kept : tokens;
}

// Catalog kind → display label. Shared so the kind-token scoring rung and
// UI chips agree on what "hotel" means.
export const KIND_LABELS = {
  hotels: 'Hotel',
  destinations: 'Destination',
  restaurants: 'Restaurant',
  bars: 'Bar',
  attractions: 'Attraction',
  experiences: 'Experience',
  shopping: 'Shopping',
};

// Build the normalized scoring document for one catalog entry. Callers pass
// whatever fields their entry shape has; missing fields index as empty and
// simply never match. `detail` is a caller-composed grab-bag string
// (highlight / entityType / priceLabel / country …) scored at the weakest
// rung. A single malformed field (null name that slipped past the catalog
// build) must not throw — normalizeIndexText coerces defensively.
export function buildSearchDoc(raw) {
  const r = raw ?? {};
  return {
    _name: normalizeIndexText(r.name ?? r.id ?? ''),
    _aliases: normalizeIndexText(
      (Array.isArray(r.aliases) ? r.aliases : []).join(' | '),
    ),
    _region: normalizeIndexText(r.region ?? ''),
    _subregion: normalizeIndexText(r.subregion ?? ''),
    _cuisine: normalizeIndexText(r.cuisine ?? ''),
    _tags: (Array.isArray(r.tags) ? r.tags : [])
      .map((t) => normalizeIndexText(t))
      .filter(Boolean),
    _detail: normalizeIndexText(r.detail ?? ''),
    _kind: String(r.type ?? ''),
  };
}

// Single-edit equality: 1 substitution, insertion, or deletion.
function withinOneEdit(a, b) {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let diffs = 0;
    for (let i = 0; i < la; i++) if (a[i] !== b[i] && ++diffs > 1) return false;
    return true;
  }
  const s = la < lb ? a : b;
  const t = la < lb ? b : a;
  let i = 0, j = 0, used = false;
  while (i < s.length && j < t.length) {
    if (s[i] === t[j]) { i++; j++; }
    else if (used) return false;
    else { used = true; j++; }
  }
  return true;
}

// Typo tolerance only for 5+ char tokens — below that, 1-edit distance
// false-positives too much ("aman" → "amen", "iran", …).
function fuzzyWordHit(haystack, token) {
  if (token.length < 5) return false;
  const words = haystack.split(/[\s\-_/().,&'"]+/).filter(Boolean);
  for (const w of words) {
    if (w.length < 4) continue;
    if (withinOneEdit(w, token)) return true;
  }
  return false;
}

// Field-weighted score for one token against one doc. 0 = no match.
// Rungs are checked in strictly descending score order, so the returned
// value is the token's best rung. The ladder is the union of the historical
// mobile + web-SearchModal ladders (kept in their original precedence):
//   name:    exact 200 · prefix 130 · word-boundary 80 · substring 50
//   aliases: word-boundary 75 · substring 45   (romanizations + native script)
//   region/subregion: exact 70 · substring 40
//   tag exact 35 · cuisine substring 30 · fuzzy name 25 · tag substring 20
//   fuzzy region 18 · kind token 15 · detail substring (4+ chars) 10
//
// A Latin token of three letters or fewer ("use", "spa", "bar") matches only
// at the start of a word, never mid-word: "day use onsen" matched "Museum"
// through "use" (web user test 2026-09-29). CJK tokens are exempt, since
// Japanese has no word breaks.
export function scoreToken(doc, token) {
  if (!token) return 0;
  if (doc._name === token) return 200;
  if (doc._name.startsWith(token)) return 130;
  const wb = ' ' + doc._name + ' ';
  if (wb.includes(' ' + token) || wb.includes(token + ' ')) return 80;
  const inner = !(token.length <= 3 && /^[a-z0-9]+$/.test(token));
  if (inner && doc._name.includes(token)) return 50;
  if (doc._aliases) {
    const wa = ' ' + doc._aliases + ' ';
    if (wa.includes(' ' + token)) return 75;
    if (inner && doc._aliases.includes(token)) return 45;
  }
  if (doc._region === token || doc._subregion === token) return 70;
  if (inner && (doc._region.includes(token) || doc._subregion.includes(token))) return 40;
  for (const t of doc._tags) if (t === token) return 35;
  if (doc._cuisine && (inner ? doc._cuisine.includes(token) : (' ' + doc._cuisine).includes(' ' + token))) return 30;
  if (fuzzyWordHit(doc._name, token)) return 25;
  for (const t of doc._tags) if (inner ? t.includes(token) : t.startsWith(token)) return 20;
  if (fuzzyWordHit(doc._region + ' ' + doc._subregion, token)) return 18;
  if (
    doc._kind &&
    (doc._kind === token ||
      (KIND_LABELS[doc._kind] ?? '').toLowerCase() === token)
  ) {
    return 15;
  }
  if (token.length >= 4 && doc._detail && doc._detail.includes(token)) return 10;
  return 0;
}

// AND-match: every token must score > 0 somewhere in the doc.
// For filter-only surfaces (Browse's ?q= pre-filter) that don't rank.
export function docMatches(doc, tokens) {
  for (const t of tokens) {
    if (scoreToken(doc, t) === 0) return false;
  }
  return true;
}

// Rank a list of rows by summed token score. Every token must match
// (AND); rows failing any token are dropped. A mild short-name boost
// keeps "Tokyo" above "Tokyo Marriott Hotel" on a one-word query. Sort
// is stable, so equal-score rows keep caller order.
export function rankDocs(rows, tokens, docOf) {
  const hits = [];
  for (const row of rows) {
    const doc = docOf(row);
    let total = 0;
    let all = true;
    for (const t of tokens) {
      const s = scoreToken(doc, t);
      if (s === 0) { all = false; break; }
      total += s;
    }
    if (!all) continue;
    total += Math.max(0, 30 - doc._name.length);
    hits.push({ row, score: total });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits;
}
